import React, { ReactNode } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Drawer, Box, Fab } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@untitledui-pro/icons/line';

interface ColumnProps {
    alignment: 'left' | 'right';
    header?: ReactNode;
    children?: ReactNode;
    className?: string;
    isCollapsed?: boolean;
    onToggle?: () => void;
}

const Column: React.FC<ColumnProps> = ({ alignment, header, children, className, isCollapsed = false, onToggle }) => {
    const theme = useTheme();
    const open = !isCollapsed;
    const isLeft = alignment === 'left';
    const Arrow = isLeft ? (open ? ChevronLeft : ChevronRight) : open ? ChevronRight : ChevronLeft;
    const isLight = theme.palette.mode === 'light';
    const columnBackground = alpha(theme.palette.background.default, isLight ? 0.45 : 0.3);
    const panelBackground = alpha(theme.palette.background.paper, isLight ? 0.26 : 0.5);
    const columnBorder = alpha(isLight ? theme.palette.common.black : theme.palette.common.white, 0.08);
    const panelBorder = isLight ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.common.white, 0.08);

    //------- Legend ----------------------------
    const paperRef = React.useRef<HTMLDivElement | null>(null);
    const handlePaperWheel = React.useCallback((event: React.WheelEvent<HTMLDivElement>) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) event.preventDefault();
    }, []);

    React.useLayoutEffect(() => {
        const root = document.documentElement;
        const cssVar = alignment === 'right' ? '--right-drawer-visible' : '--left-drawer-visible';
        const update = () => {
            if (!open) {
                root.style.setProperty(cssVar, '0px');
                return;
            }
            const el = paperRef.current;
            const w = el ? el.getBoundingClientRect().width : 0;
            root.style.setProperty(cssVar, `${Math.max(0, w)}px`);
        };

        update();

        const ro = paperRef.current ? new ResizeObserver(update) : null;
        if (paperRef.current && ro) ro.observe(paperRef.current);

        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('resize', update);
            if (ro && paperRef.current) ro.unobserve(paperRef.current);
        };
    }, [alignment, open]);

    return (
        <>
            <Drawer
                anchor={alignment}
                variant="persistent"
                open={open}
                ModalProps={{ keepMounted: true }}
                className={`gui-drawer gui-drawer--${alignment} ${className || ''}`}
                slotProps={{
                    paper: {
                        className: `gui-drawer-paper gui-drawer-paper--${alignment}`,
                        sx: {
                            '--gui-column-bg': columnBackground,
                            '--gui-column-color': theme.palette.text.primary,
                            '--gui-column-border': columnBorder,
                            '--gui-panel-bg': panelBackground,
                            '--gui-panel-border': panelBorder,
                            '--gui-panel-color': theme.palette.text.primary,
                        },
                        // Reference for the legend
                        ref: (node: any) => {
                            paperRef.current = node as HTMLDivElement | null;
                        },
                        onWheel: handlePaperWheel,
                    },
                }}
                transitionDuration={{ appear: 0, enter: 200, exit: 200 }}
            >
                <Box className="gui-drawer-inner">
                    <div className="column-header">{header}</div>
                    <div className="panel-body">{children}</div>
                </Box>
            </Drawer>

            <Box
                className={`drawer-handle ${alignment} ${open ? 'open' : 'closed'} ${className || ''}`}
                aria-label={open ? 'Collapse panel' : 'Expand panel'}
            >
                <Fab className="drawer-handle-fab" size="small" onClick={onToggle} aria-label={open ? 'Collapse' : 'Expand'}>
                    <Arrow />
                </Fab>
            </Box>
        </>
    );
};

export default Column;
